<template>
  <div class="p-2 sm:p-3">
    <div class="flex justify-center flex-wrap">
      <div class="min-w-full sm:min-w-30 max-w-4xl flex-1 m-2">
        <div class="bg-white rounded-lg shadow-card px-4 py-8 sm:p-12">
          <div class="flex flex-col md:flex-row items-center justify-between">
            <h1 class="font-semibold text-2xl lg:text-3xl">
              Messages
            </h1>
            <button class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 w-full md:w-auto mt-3 md:mt-0"
                    @click="onClickCreateNew">
              + Add New Message
            </button>
          </div>
          <section class="mt-10">
            <table class="w-full">
              <thead v-if="messages.length > 0" class="hidden md:table-header-group text-sm">
              <tr>
                <th class="w-20 text-xs text-gray-600 font-semibold py-3 text-left">QUEUE</th>
                <th class="text-xs text-gray-600 font-semibold py-3 text-left">MESSAGE</th>
                <th class="w-24 hidden md:table-cell"></th>
              </tr>
              </thead>
              <tbody v-if="progressLoading">
              <tr>
                <td colspan="5">
                  <div class="text-center text-gray-600 my-2 p-5">
                    <Spinner class="mx-auto" orange/>
                  </div>
                </td>
              </tr>
              </tbody>
              <tbody v-else-if="messages.length === 0">
              <tr>
                <td colspan="5">
                  <div class="text-center text-gray-600 my-2 p-5">
                    <span class="block text-left text-gray-600 font-semibold text-center">
                      Click 'Add New Message'
                    </span>
                    <span class="block text-left text-gray-600 font-semibold text-center">
                      to create the first message
                    </span>
                  </div>
                </td>
              </tr>
              </tbody>
              <tbody v-else>
              <tr v-for="(message, index) in messages" :key="index">
                <td class="py-5 align-top">
                  <div class="flex items-center">
                    <div class="w-5 h-5 rounded-full border border-gray-500 flex items-center">
                      <span class="mx-auto text-gray-500 text-xs">{{ index + 1 }}</span>
                    </div>
                    <div class="md:hidden font-semibold ml-4 flex-1">{{ message.name }}</div>
                    <div class="md:hidden text-right">
                      <button class="btn p-3" @click="onClickEditMessage(message)">
                        <img alt="BuildaBox Order Details Categories Page" src="@/assets/icons/edit-pencil.svg"/>
                      </button>
                      <button class="btn p-3 ml-2" @click="onClickRemoveMessage(message)">
                        <img alt="BuildaBox Order Details Categories Page" src="@/assets/icons/ic_remove.svg"/>
                      </button>
                    </div>
                  </div>
                  <div class="md:hidden ml-10 mr-5 mt-2">
                    <div class="mt-2 text-gray-600 text-gray-600">{{ message.description }}</div>
                  </div>
                </td>
                <td class="py-5 hidden md:table-cell text-left">
                  <div class="font-semibold">
                    {{ message.name }}
                  </div>
                  <div class="font-normal text-gray-600 mt-2">
                    {{ message.description }}
                  </div>
                </td>
                <td class="py-5 hidden md:table-cell">
                  <button class="btn p-3" @click="onClickEditMessage(message)">
                    <img alt="BuildaBox Order Details Categories Page" src="@/assets/icons/edit-pencil.svg"/>
                  </button>
                  <button class="btn p-3 ml-2" @click="onClickRemoveMessage(message)">
                    <img alt="BuildaBox Order Details Categories Page" src="@/assets/icons/ic_remove.svg"/>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </section>
          <section class="mt-16">
            <div class="flex flex-col-reverse sm:flex-row justify-between">
              <button
                  class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 mt-5 sm:mt-0"
                  @click="$emit('back')"
              >
                <span>Back</span>
              </button>
              <div>
                <button
                    v-if="order.from === 'buildabox'"
                    class="btn btn-secondary font-semibold rounded-lg px-10 min-w-10 mr-3"
                    @click="$emit('submit')"
                >
                  <span>Skip</span>
                </button>
                <button
                    :disabled="!canProceedToNextStep"
                    class="btn btn-primary font-semibold rounded-lg px-10 min-w-10"
                    @click="$emit('submit')"
                >
                  <span v-if="!progressSaving">Next</span>
                  <Spinner v-if="progressSaving" class="mx-auto"/>
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
      <div class="bg-white rounded-lg shadow-card p-8 self-start max-w-sm sm:p-12 m-2">
        <img alt="BuildaBox Order Edit Page" class="mb-4 " height="32" src="@/assets/icons/info.svg" width="32">
        <div class="mb-3">
          <h1 class="font-semibold text-lg">Helper Info Box</h1>
        </div>
        <p class="pb-10">
          The Messages tool provides event and program organizers with the tools to keep attendees informed and
          organized, to prompt social media marketing, request information and more.
          <br/><br/>
          Build messages by selecting the <br/><b>Add New Message</b> button. These can be edited at anytime, even
          after the event has begun.
          <br/><br/>
          After the messages are sent, event organizers can monitor tracking data to verify receipt, views and more.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/common/Spinner'
import Api from '@/api/api_v1.0'

export default {
  components: { Spinner },
  data () {
    return {
      messages       : [],
      showModal      : false,
      progressLoading: false,
      progressSaving : false
    }
  },
  computed: {
    order () {
      return this.$store.state.currentEditingOrder
    },
    canProceedToNextStep () {
      return true
    }
  },
  mounted () {
    this.loadMessages()
  },
  methods: {
    loadMessages () {
      this.progressLoading = true
      Api.getMessagesOfOrder(this.order.uuid).then((result) => {
        this.progressLoading = false

        // Save messages to the store for caching.
        this.messages = result.data.data
        const order = this.order
        order.messages = this.messages
        this.$store.commit('setCurrentEditingOrder', order)
      }).catch(() => {
        this.progressLoading = false
      })
    },
    onClickCreateNew () {
      this.$router.push(`/orders/${this.order.uuid}/messages/create`)
    },
    onClickEditMessage (message) {
      this.$router.push(`/orders/${this.order.uuid}/messages/${message.id}/edit`)
    },
    onClickRemoveMessage (message) {
      this.$swal({
        title            : 'Remove Message',
        text             : `Are you sure to remove the message - ${message.name}?`,
        confirmButtonText: 'Yes',
        cancelButtonText : 'No',
        showCancelButton : true
      }).then((result) => {
        if (result.isConfirmed) {
          this.removeMessage(message)
        }
      })
    },
    async removeMessage (message) {
      this.progressLoading = true
      Api.deleteMessageFromOrder(this.order.uuid, message).then(() => {
        this.progressLoading = false
        this.loadMessages()
      }).catch(() => {
        this.progressLoading = false
      })
    }
  }
}
</script>